import { IThemeService } from '@hibro/shared';
import { type ReactNode } from 'react';

import {
  CustomHeader,
  type WindowControlsBridge,
} from '../../components/custom-header/custom-header';
import { useWorkbenchLayout } from '../layout-context';
import type { HeaderCreateProps } from './header-create';
import { HeaderRight } from './header-right';
import { HeaderSearch, type HeaderSearchProps } from './header-search';

export interface HeaderSlotProps {
  bridge?: WindowControlsBridge;
  error: string | null;
  commandCount: number;
  viewCount: number;
  badge: number;
  online: boolean;
  themeService: IThemeService;
  onRefresh: () => void;
  onOpenWindow?: () => void;
  /** Everything the centred global search field needs (需求 FR-14 §4). */
  search: Omit<HeaderSearchProps, 'onToggleAi'>;
  /** Rows for the Create menu, from `contributes.menus` (见 `header-create`). */
  create: HeaderCreateProps;
}

/**
 * Workbench header slot (架构方案 §5.7/§5.8). Composes `CustomHeader` (which
 * owns the brand logo, OS drag region and platform window controls) with:
 * - `leftContent`  is omitted until back/forward history lands (`HeaderLeft`);
 * - `children`     = the window-centred global search field (`HeaderSearch`)
 *                    with an auxiliaryBar toggle (`CustomHeader` pins this
 *                    track to the title-bar midline);
 * - `rightContent` = `<HeaderRight>` — layout/action cluster + theme toggle.
 *
 * The search field and the Create menu are no longer behind
 * `SHOW_UNFINISHED_HEADER_CHROME`: both are wired (见 03 header 槽位).
 * Notifications still are.
 */
export function HeaderSlot({
  bridge,
  onRefresh,
  themeService,
  search,
  create,
}: HeaderSlotProps): ReactNode {
  const { enabledAreas, toggle } = useWorkbenchLayout();

  return (
    <CustomHeader
      bridge={bridge}
      rightContent={
        <HeaderRight
          themeService={themeService}
          onRefresh={onRefresh}
          create={create}
        />
      }
    >
      <HeaderSearch
        {...search}
        {...(enabledAreas.auxiliaryBar
          ? { onToggleAi: () => toggle('auxiliaryBar') }
          : {})}
      />
    </CustomHeader>
  );
}
